import { useCallback, useEffect, useState } from 'react'
import { Building2, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { TIPO_CLINICA_LABEL, type Clinica, type TipoClinica } from '@/lib/types'
import { atualizarClinica, criarClinica, listClinicas, removerClinica } from '@/services/clinicas'

const CORES = ['#0E7490', '#7C3AED', '#B45309', '#15803D', '#DC2626', '#2563EB']

export default function ClinicasPage() {
  const { current } = useAuth()
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [editId, setEditId] = useState<string | 'nova' | null>(null)
  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState<TipoClinica>('externa')
  const [cor, setCor] = useState<string>(CORES[0])
  const [busy, setBusy] = useState(false)

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    try {
      setClinicas(await listClinicas(current.workspace.id))
    } catch {
      setErro('Não foi possível carregar as clínicas.')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  function abrirNova() {
    setEditId('nova'); setNome(''); setTipo('externa'); setCor(CORES[0]); setErro(null)
  }

  function abrirEdicao(c: Clinica) {
    setEditId(c.id); setNome(c.nome); setTipo(c.tipo); setCor(c.cor ?? CORES[0]); setErro(null)
  }

  async function remover(c: Clinica) {
    if (!current) return
    if (!window.confirm(`Remover a clínica "${c.nome}"? Pacientes e consultas já vinculados mantêm o histórico.`)) return
    try {
      await removerClinica(current.workspace.id, c.id)
      await carregar()
    } catch {
      setErro('Não foi possível remover a clínica.')
    }
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || !editId) return
    setBusy(true)
    setErro(null)
    try {
      if (editId === 'nova') {
        await criarClinica({ workspaceId: current.workspace.id, nome, tipo, cor })
      } else {
        await atualizarClinica({ workspaceId: current.workspace.id, id: editId, nome, tipo, cor })
      }
      setEditId(null)
      await carregar()
    } catch {
      setErro('Não foi possível salvar. Verifique sua permissão (clinicas.gerir).')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Clínicas</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Os lugares onde você atende — origem dos seus pacientes.</p>
        </div>
        <button className="btn-primary" onClick={abrirNova}>
          <Plus size={16} aria-hidden /> Nova clínica
        </button>
      </header>

      {editId && (
        <form onSubmit={salvar} className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="cl-nome">Nome *</label>
              <input id="cl-nome" required className="field-input" value={nome} onChange={e => setNome(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="cl-tipo">Tipo *</label>
              <select id="cl-tipo" className="field-input" value={tipo} onChange={e => setTipo(e.target.value as TipoClinica)}>
                <option value="consultorio_proprio">{TIPO_CLINICA_LABEL.consultorio_proprio}</option>
                <option value="externa">{TIPO_CLINICA_LABEL.externa}</option>
              </select>
            </div>
          </div>
          <div>
            <span className="field-label">Cor de identificação</span>
            <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label="Cor da clínica">
              {CORES.map(c => (
                <button
                  key={c} type="button" onClick={() => setCor(c)}
                  role="radio" aria-checked={cor === c} aria-label={`Cor ${c}`}
                  style={{
                    width: 30, height: 30, borderRadius: 999, background: c, border: 'none',
                    outline: cor === c ? '3px solid var(--primary-soft)' : 'none',
                    boxShadow: cor === c ? `0 0 0 2px ${c}` : 'none',
                  }}
                />
              ))}
            </div>
          </div>
          {erro && <div className="field-error" role="alert">{erro}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Salvar
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditId(null)}>Cancelar</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        </div>
      ) : clinicas.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Building2 size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhuma clínica ainda</div>
          <div style={{ fontSize: 13.5, marginTop: 4 }}>Cadastre os lugares onde você atende — inclusive seu consultório próprio.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {clinicas.map(c => (
            <div key={c.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span aria-hidden style={{ width: 12, height: 12, borderRadius: 999, background: c.cor ?? 'var(--muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{c.nome}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{TIPO_CLINICA_LABEL[c.tipo]}</div>
              </div>
              {!c.ativo && <span className="badge badge-muted">Inativa</span>}
              <button
                className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                onClick={() => abrirEdicao(c)} aria-label={`Editar ${c.nome}`}
              >
                <Pencil size={14} aria-hidden /> Editar
              </button>
              <button
                className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                onClick={() => remover(c)} aria-label={`Remover ${c.nome}`}
              >
                <Trash2 size={14} aria-hidden /> Remover
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
